import { registerLocaleData } from '@angular/common';
import {
  HttpBackend,
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import localeBs from '@angular/common/locales/bs';
import localeHr from '@angular/common/locales/hr';
import { APP_INITIALIZER, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  CalendarDateFormatter,
  CalendarModule,
  CalendarNativeDateFormatter,
  DateAdapter,
  DateFormatterParams,
} from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/moment';
import moment from 'moment';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzConfig, NZ_CONFIG } from 'ng-zorro-antd/core/config';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { hr_HR, NZ_I18N } from 'ng-zorro-antd/i18n';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { Observable, ObservableInput, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BASE_PATH } from './backend';
import { DialogModule } from './core/dialog/dialog.module';
import { ApiInterceptor } from './core/interceptors/api.interceptor';
import { PublicLayoutComponent } from './core/layouts/public-layout/public-layout.component';
import { SecureLayoutComponent } from './core/layouts/secure-layout/secure-layout.component';
import { ChunkPipe } from './core/pipes/chunk.pipe';
import { DurationPipe } from './core/pipes/duration.pipe';
import { ConfigService } from './core/services/config.service';
import { I18nService } from './core/services/i18n.service';
import { CategoriesComponent } from './pages/categories/categories.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { DonationsComponent } from './pages/donations/donations.component';
import { NeedsComponent } from './pages/needs/needs.component';
import { FamiliesComponent } from './pages/families/families.component';
import { ListsComponent } from './pages/lists/lists.component';
import { LoginComponent } from './pages/login/login.component';
import { MembersComponent } from './pages/memberes/members.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { OrganisationMembersComponent } from './pages/organisation-members/organisation-members.component';
import { UsersComponent } from './pages/users/users.component';
import { SpinnerComponent } from './shared/components/spinner/spinner.component';
import { ConfirmDialogComponent } from './shared/dialogs/confirm-dialog/confirm-dialog.component';
import { SaveCategoryDialogComponent } from './shared/dialogs/save-category-dialog/save-category-dialog.component';
import { SaveDonationDialogComponent } from './shared/dialogs/save-donation-dialog/save-donation-dialog.component';
import { SaveNeedDialogComponent } from './shared/dialogs/save-need-dialog/save-need-dialog.component';
import { SaveFamilyDialogComponent } from './shared/dialogs/save-family-dialog/save-family-dialog.component';
import { SaveListDialogComponent } from './shared/dialogs/save-list-dialog/save-list-dialog.component';
import { SaveMemberDialogComponent } from './shared/dialogs/save-member-dialog/save-member-dialog.component';
import { SaveNoteDialogComponent } from './shared/dialogs/save-note-dialog/save-note-dialog.component';
import { OrgMemberDialogComponent } from './shared/dialogs/save-org-member-dialog/save-org-member-dialog.component';
import { SaveUserDialogComponent } from './shared/dialogs/save-user-dialog/save-user-dialog.component';

export function momentAdapterFactory() {
  return adapterFactory(moment);
}

class CustomDateFormatter extends CalendarNativeDateFormatter {
  public dayViewHour({ date, locale }: DateFormatterParams): string {
    return new Intl.DateTimeFormat(locale, { hour: 'numeric' }).format(date);
  }
  public weekViewColumnSubHeader({
    date,
    locale,
  }: DateFormatterParams): string {
    return moment(date).format('D. MMM');
  }
}

registerLocaleData(localeHr);
registerLocaleData(localeBs);

moment.updateLocale('bs', {
  week: {
    dow: 1,
    doy: 0,
  },
});

const ngZorroConfig: NzConfig = {
  form: {
    nzAutoTips: {
      default: {
        required: 'Field is required',
        email: 'Input is not valid email',
        minLength: 'Input doesn\'t have min length required',
        exactLength: 'Input doesn\'t have correct length required',
        emailInUse: 'Email address is in use',
        regex: 'Input doesn\'t have correct format',
      },
    },
  },
};

@NgModule({
  declarations: [
    AppComponent,
    NotFoundComponent,
    LoginComponent,
    DashboardComponent,
    PublicLayoutComponent,
    SecureLayoutComponent,
    ConfirmDialogComponent,
    ChunkPipe,
    DurationPipe,
    UsersComponent,
    SpinnerComponent,
    FamiliesComponent,
    DonationsComponent,
    NeedsComponent,
    ListsComponent,
    SaveUserDialogComponent,
    SaveFamilyDialogComponent,
    SaveMemberDialogComponent,
    SaveDonationDialogComponent,
    SaveNeedDialogComponent,
    OrganisationMembersComponent,
    OrgMemberDialogComponent,
    SaveListDialogComponent,
    SaveNoteDialogComponent,
    MembersComponent,
    CategoriesComponent,
    SaveCategoryDialogComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    TranslateModule.forRoot(),
    TranslateModule.forRoot({
      defaultLanguage: 'bs',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    FormsModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    NzInputModule,
    NzCardModule,
    NzFormModule,
    NzButtonModule,
    NzSpaceModule,
    NzTypographyModule,
    NzPopoverModule,
    NzSpinModule,
    NzInputNumberModule,
    NzStatisticModule,
    NzTableModule,
    NzSelectModule,
    NzMessageModule,
    NzEmptyModule,
    NzCollapseModule,
    NzTabsModule,
    NzListModule,
    NzRadioModule,
    NzDropDownModule,
    NzToolTipModule,
    NzDatePickerModule,
    CalendarModule.forRoot(
      {
        provide: DateAdapter,
        useFactory: momentAdapterFactory,
      },
      {
        dateFormatter: {
          provide: CalendarDateFormatter,
          useClass: CustomDateFormatter,
        },
      }
    ),
    DialogModule,
    CalendarModule,
  ],
  providers: [
    ApiInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
    { provide: NZ_I18N, useValue: hr_HR },
    { provide: NZ_CONFIG, useValue: ngZorroConfig },
    {
      provide: APP_INITIALIZER,
      useFactory: i18nFactory,
      deps: [TranslateService, Injector],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: loadConfig,
      multi: true,
      deps: [HttpBackend, ConfigService],
    },
    {
      provide: BASE_PATH,
      useFactory: getApiUrl,
      deps: [ConfigService],
    },
    {
      provide: BASE_PATH,
      useValue: 'replaceMeInInterceptor',
    },
    {
      provide: LOCALE_ID,
      useValue: 'bs',
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function i18nFactory(
  translateService: TranslateService,
  injector: Injector
) {
  return new I18nService(translateService).initialize2(
    translateService,
    injector
  );
}

export function getApiUrl(config: ConfigService) {
  return config.apiUrl;
}

export function loadConfig(
  handler: HttpBackend,
  config: ConfigService
): () => Promise<boolean> {
  return (): Promise<boolean> => {
    return new Promise<boolean>((resolve: (a: boolean) => void): void => {
      const http = new HttpClient(handler);
      http
        .get('./configs/config.json')
        .pipe(
          map((x: any) => {
            config.apiUrl = x.apiUrl;
            resolve(true);
          }),
          catchError(
            (
              x: { status: number },
              caught: Observable<void>
            ): ObservableInput<{}> => {
              if (x.status !== 404) {
                resolve(false);
              }

              console.error('Can not find config.json');
              resolve(true);
              return of({});
            }
          )
        )
        .subscribe();
    });
  };
}
